import PropTypes from "prop-types";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import { Chip, Grid, Icon, MenuItem, OutlinedInput, Select, Stack } from "@mui/material";
import { useRef, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import StyledTextarea from "components/StyledTextarea";
import dayjs from 'dayjs';
import { DatePicker } from "@mui/x-date-pickers";
import Dropzone from "react-dropzone";
import StyledImagePreview from "components/StyledImagePreview";
import Loader from "components/loader";
import SoftButton from "components/SoftButton";

function JobJobAddEdit({ currentJobData, allJobCategories, noJobImage }) {
    const editorRef = useRef(null);
    const [defaultCategoryId, setDefaultCategoryId] = useState(currentJobData.job_category_id ?? 0);
    const [allKeywords, setAllKeywords] = useState(currentJobData.seo_keywords ?? []);
    const defaultImagePreview = noJobImage;
    const defaultImageName = 'No Image Available';
    const [imagePreview, setImagePreview] = useState(
        currentJobData.image !== '' && currentJobData.image !== null
            ? currentJobData.image
            : defaultImagePreview
    );
    const [imageName, setImageName] = useState(
        currentJobData.image_name !== '' && currentJobData.image_name !== null
            ? currentJobData.image_name
            : defaultImageName
    );
    const [isDropOverImage, setIsDropOverImage] = useState(false);
    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };
    const [imageLoader, setImageLoader] = useState(false);
    const handleOnDrag = (acceptedFiles) => {
        setImageLoader(true);
        const reader = new FileReader();
        reader.onload = async function () {
            const jobBlob = new Blob([reader.result]);
            setImagePreview(URL.createObjectURL(jobBlob));
            setImageLoader(false);
            setIsDropOverImage(false);
        };
        reader.readAsArrayBuffer(acceptedFiles[0]);
        setImageName(acceptedFiles[0].name);
        currentJobData['image'] = acceptedFiles[0];
        currentJobData['image_name'] = acceptedFiles[0].name;
        currentJobData['isNewImage'] = true;
    };
    const handleJobImageRemove = (event) => {
        setImageLoader(true);
        setImagePreview(defaultImagePreview);
        setImageName(defaultImageName);
        setImageLoader(false);
    }

    return <>
        <SoftBox component="form" role="form" style={{
            "margin": "0 16px"
        }}>
            <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="stretch"
                spacing={1}>
                <Grid item xs={12} sm={12}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Job Title
                            </SoftTypography>
                        </SoftBox>
                        <SoftInput
                            type="text"
                            placeholder="Job Title"
                            defaultValue={currentJobData.title}
                            onChange={(event) => currentJobData.title = event.target.value} />
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Job Category
                            </SoftTypography>
                        </SoftBox>
                        <Select
                            displayEmpty
                            value={defaultCategoryId}
                            onChange={(event) => {
                                currentJobData.job_category_id = event.target.value;
                                setDefaultCategoryId(currentJobData.job_category_id);
                            }}
                            input={<OutlinedInput />}
                            MenuProps={MenuProps}
                        >
                            <MenuItem value="0">
                                Select Job Category
                            </MenuItem>
                            {allJobCategories.map((singleJob) => (
                                <MenuItem
                                    key={singleJob.id}
                                    value={singleJob.id}
                                >
                                    {singleJob.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Publish Date
                            </SoftTypography>
                        </SoftBox>
                        <DatePicker
                            defaultValue={dayjs(currentJobData.publish_date)}
                            onChange={(newValue) => {
                                currentJobData.publish_date = newValue.format('YYYY-MM-DD');
                                currentJobData.isPublishDateChange = true;
                            }}
                        />
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Job Image
                            </SoftTypography>
                        </SoftBox>
                        <SoftBox mb={1} style={{ 'padding': '15px 15px 15px 0' }}>
                            {
                                imageName === 'No Image Available' ?
                                    <Dropzone
                                        onDragEnter={() => {
                                            setIsDropOverImage(true);
                                        }}
                                        onDragLeave={() => {
                                            setIsDropOverImage(false);
                                        }}
                                        onDrop={handleOnDrag}>
                                        {({ getRootProps, getInputProps }) => (
                                            <section style={{
                                                'width': '100%',
                                                'height': '160px',
                                                'display': 'flex',
                                                'alignItems': 'center',
                                                'border': '2px dashed #344767',
                                                'borderRadius': '16px'
                                            }}>
                                                <div {...getRootProps()} style={{
                                                    'width': 'inherit',
                                                    'height': 'inherit',
                                                    'display': 'flex',
                                                    'alignItems': 'center',
                                                    'textAlign': 'center',
                                                }}>
                                                    <input {...getInputProps()} />
                                                    <p style={{
                                                        'margin': '32px',
                                                        'width': 'inherit'
                                                    }}>
                                                        {
                                                            isDropOverImage ?
                                                                'Drop Here' :
                                                                'Drag & drop some files here, or click to select files'
                                                        }
                                                    </p>
                                                </div>
                                            </section>
                                        )}
                                    </Dropzone> :
                                    !imageLoader && <SoftBox mb={2} style={{
                                        'border': '2px dashed #344767',
                                        'borderRadius': '16px',
                                        'height': '64px',
                                        'padding': '0 16px',
                                        'display': 'flex',
                                        'alignItems': 'center',
                                    }}>
                                        <SoftBox mb={1} style={{ 'flex': 2 }}>
                                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                {imageName}
                                            </SoftTypography>
                                        </SoftBox>
                                        <SoftButton variant="gradient" color="error" iconOnly={true} onClick={handleJobImageRemove}>
                                            <Icon sx={{ cursor: "pointer", fontWeight: "bold" }} fontSize="small" onClick={handleJobImageRemove}>
                                                delete
                                            </Icon>
                                        </SoftButton>
                                    </SoftBox>
                            }
                            {
                                imageLoader && <Loader classes='black' />
                            }
                        </SoftBox>
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Preview Image
                            </SoftTypography>
                        </SoftBox>
                        <SoftBox mb={1} style={{ 'padding': '15px 15px 15px 0' }}>
                            <StyledImagePreview
                                imagePreview={imagePreview}
                                imageName={imageName} />
                        </SoftBox>
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Description
                            </SoftTypography>
                        </SoftBox>
                        <Editor
                            apiKey={process.env.REACT_APP_TINY_MCS}
                            onInit={(evt, editor) => editorRef.current = editor}
                            initialValue={currentJobData.description}
                            init={{
                                height: 300,
                                menubar: true,
                                plugins: [
                                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                                ],
                                toolbar: 'undo redo | blocks | ' +
                                    'bold italic forecolor | alignleft aligncenter ' +
                                    'alignright alignjustify | bullist numlist outdent indent | ' +
                                    'removeformat | help',
                                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                            }}
                            onChange={(event) => currentJobData.description = event.target.getContent()}
                        />
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Additional Tags
                            </SoftTypography>
                        </SoftBox>
                        <Editor
                            apiKey={process.env.REACT_APP_TINY_MCS}
                            onInit={(evt, editor) => editorRef.current = editor}
                            initialValue={currentJobData.additional_tags}
                            init={{
                                height: 300,
                                menubar: true,
                                plugins: [
                                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                                ],
                                toolbar: 'undo redo | blocks | ' +
                                    'bold italic forecolor | alignleft aligncenter ' +
                                    'alignright alignjustify | bullist numlist outdent indent | ' +
                                    'removeformat | help',
                                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                            }}
                            onChange={(event) => currentJobData.additional_tags = event.target.getContent()}
                        />
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Custom Script
                            </SoftTypography>
                        </SoftBox>
                        <StyledTextarea
                            aria-label="minimum height"
                            minRows={5}
                            placeholder="Custom Script"
                            defaultValue={currentJobData.custom_script}
                            onChange={(event) => currentJobData.custom_script = event.target.value} />
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Short Description
                            </SoftTypography>
                        </SoftBox>
                        <StyledTextarea
                            aria-label="minimum height"
                            minRows={5}
                            placeholder="Short Description"
                            defaultValue={currentJobData.short_description}
                            onChange={(event) => currentJobData.short_description = event.target.value} />
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                SEO Title
                            </SoftTypography>
                        </SoftBox>
                        <SoftInput
                            type="text"
                            placeholder="SEO Title"
                            defaultValue={currentJobData.seo_title}
                            onChange={(event) => currentJobData.seo_title = event.target.value} />
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                SEO Keywords
                            </SoftTypography>
                        </SoftBox>
                        <SoftInput
                            type="text"
                            placeholder="SEO Keywords"
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    currentJobData.seo_keywords.push(event.target.value);
                                    setAllKeywords(currentJobData.seo_keywords);
                                    event.target.value = '';
                                }
                            }} />
                        {
                            allKeywords.length > 0 && <SoftBox mt={1}>
                                <Stack direction="row" spacing={1} style={{ 'flexWrap': 'wrap' }}>
                                    {
                                        allKeywords.map((singleSeoKeyword, index) => (
                                            <Chip
                                                label={singleSeoKeyword}
                                                onDelete={() => {
                                                    currentJobData.seo_keywords = currentJobData.seo_keywords.filter((seoKeyword) => seoKeyword !== singleSeoKeyword);
                                                    setAllKeywords(currentJobData.seo_keywords);
                                                }}
                                                key={singleSeoKeyword + '_' + index} />
                                        ))
                                    }
                                </Stack>
                            </SoftBox>
                        }
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                SEO Description
                            </SoftTypography>
                        </SoftBox>
                        <StyledTextarea
                            aria-label="minimum height"
                            minRows={5}
                            placeholder="SEO Description"
                            defaultValue={currentJobData.seo_description}
                            onChange={(event) => currentJobData.seo_description = event.target.value} />
                    </SoftBox>
                </Grid>
            </Grid>
        </SoftBox >
    </>;
}
JobJobAddEdit.defaultProps = {
    currentJobData: {},
    allJobCategories: [],
    noJobImage: ''
};
JobJobAddEdit.propTypes = {
    currentJobData: PropTypes.any.isRequired,
    allJobCategories: PropTypes.array.isRequired,
    noJobImage: PropTypes.string.isRequired
};

export default JobJobAddEdit;